import CustomPreloader from '@components/CustomPreloader'
import {
  CheckoutFlowStep,
  isCheckoutMethodFieldReadOnly,
  selectCurrencyOptions,
  useAppSelector,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useLanguageParam,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, CountryChoice, SupportedBank } from 'mmfintech-checkout-commons/types'
import { isEmptyString, isValidArray, parseJson, tr, translateCountryName } from 'mmfintech-commons'
import { Button, ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { CheckoutContainer, CheckoutContent, CustomInput, SelectMethod } from '../../components'
import settings from '../../settings'
import { DepositResult } from '../DepositResult'
import styles from './Checkout.module.scss'
import { CancelContainer, PaymentContainer } from './Checkout.styled'
import { MerchantPane } from './MerchantPane'

const Checkout = () => {
  useLanguageParam(settings.languages)
  const sessionId = useSessionId()

  const {
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  const history = useHistory()
  useUnloadBeacon(sessionId)

  const currencies = useAppSelector(selectCurrencyOptions)

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest()

  const {
    step,
    // setStep,
    fieldValues,
    status,
    countryCode,
    setCountryCode,
    paymentMethod,
    setPaymentMethod,
    handleStartOver,
    handlePay,
    reloadSession
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD
  })

  const { banks } = useBanksRequest()

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    return false
  }

  const handleBankSelected = (code: string) => {
    void trackEvent(sessionId, 'bank_selected', code)
  }

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult, session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  useEffect(() => {
    if (checkoutPayError) {
      const { errorKey } = checkoutPayError || {}
      if (errorKey === 'MERCHANTS.ERROR.PAYMENT_ALREADY_COMPLETED') {
        reloadSession()
      }
      fieldValues.handleErrors(checkoutPayError)
    }
    // eslint-disable-next-line
  }, [checkoutPayError])

  return (
    <CheckoutContainer className='speedy-background-2'>
      <CheckoutContent>
        <ErrorDisplay error={[sessionError, checkoutPayError]} />

        {(step === CheckoutFlowStep.SELECT_METHOD || step === CheckoutFlowStep.ADDITIONAL_FIELDS) && (
          <MerchantPane session={session} />
        )}

        {sessionFetching && (
          <PaymentContainer>
            <CustomPreloader />
          </PaymentContainer>
        )}

        {!sessionFetching &&
          status === 'IDLE' &&
          (step === CheckoutFlowStep.SELECT_METHOD || step === CheckoutFlowStep.ADDITIONAL_FIELDS) && (
            <>
              <PaymentContainer>
                {checkoutCountries?.length > 1 ? (
                  <div className='mb-3'>
                    <CustomInput
                      type='select'
                      placeholder={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
                      options={checkoutCountries?.map((country: CountryChoice) => ({
                        value: country.countryCode,
                        label: country.name
                      }))}
                      value={countryCode}
                      onChange={(_name: string, value: string) => setCountryCode(value)}
                      hideErrorLine
                    />
                  </div>
                ) : (
                  <div className='mb-3'>
                    <CustomInput
                      type='static'
                      color='alternative'
                      label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                      value={translateCountryName(countryCode)}
                      hideErrorLine
                    />
                  </div>
                )}

                <SelectMethod
                  className='use-grid'
                  method={paymentMethod}
                  setMethod={setPaymentMethod}
                  countryCode={countryCode}
                />

                {isValidArray(fieldValues.fields) ? (
                  <>
                    <div className='subtitle mt-3 mb-1'>
                      {tr('CHECKOUT.PAYMENT.DETAILS_CAPTION', 'Enter your details')}
                    </div>

                    {fieldValues.fields.map((field: CheckoutMethodField) => {
                      if (fieldValues.isFieldHidden(field)) {
                        return null
                      }

                      if (field.name === 'bankChoiceId') {
                        return (
                          <CustomInput
                            key={field.name}
                            type='select'
                            label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                            {...fieldValues.registerInput(field.name, handleBankSelected)}
                            options={banks?.map((bank: SupportedBank) => {
                              const { bankChoiceId, name } = bank
                              return { value: bankChoiceId, label: name }
                            })}
                            hideErrorLine
                          />
                        )
                      }

                      if (field.name === 'currency') {
                        return (
                          <CustomInput
                            key={field.name}
                            type='select'
                            label={
                              field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                            }
                            {...fieldValues.registerInput(field.name)}
                            options={currencies?.map((currency: string) => ({ value: currency, label: currency }))}
                            hideErrorLine
                          />
                        )
                      }

                      if (field.name === 'documentId' && paymentMethod === 'PIX') {
                        return (
                          <CustomInput
                            key={field.name}
                            type={field.sensitive ? 'password' : 'text'}
                            label={tr(
                              'MERCHANTS.CHECKOUT.FIELDS.GENERAL.CPF_CNPJ',
                              field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                            )}
                            {...fieldValues.registerShort(field.name)}
                            onChange={(name: string, value: string) =>
                              fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))
                            }
                            required={fieldValues.isFieldRequired(field)}
                            hideErrorLine
                            autoComplete='off'
                          />
                        )
                      }

                      return (
                        <CustomInput
                          key={field.name}
                          type={field.sensitive ? 'password' : 'text'}
                          label={
                            field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                          }
                          {...fieldValues.registerInput(field.name)}
                          readOnly={isCheckoutMethodFieldReadOnly(field)}
                          autoComplete='off'
                          hideErrorLine
                        />
                      )
                    })}
                  </>
                ) : null}

                <div className='buttons'>
                  <Button
                    type='button'
                    color='primary'
                    text={tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')}
                    onClick={handlePay}
                    disabled={isEmptyString(paymentMethod)}
                    loading={checkoutPayFetching}
                    data-test='button-pay'
                    iconPosition='right'
                    icon='right-arrow'
                  />
                </div>
              </PaymentContainer>

              {Boolean(session?.showCancelButton) && (
                <CancelContainer>
                  <Link to='#' onClick={handleCancelClick}>
                    {tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
                  </Link>
                </CancelContainer>
              )}

              {Boolean(session?.returnUrl) && (
                <div className={styles['checkout-footer']}>
                  <a href={session?.returnUrl} target='_top'>
                    &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                  </a>
                </div>
              )}
            </>
          )}

        {step === CheckoutFlowStep.DEPOSIT_RESULT && (
          <DepositResult response={prepareResponse()} sessionId={sessionId} onBack={handleStartOver} />
        )}
      </CheckoutContent>
    </CheckoutContainer>
  )
}

export default Checkout
